<template>
  <div>
    <slot
      :display-date="displayDate"
      :errors="errors"
      :fallback-results="fallbackResults"
      :handle-next="handleNext"
      :handle-prev="handlePrev"
      :has-fallback-results="hasFallbackResults"
      :has-name="hasName"
      :has-results="hasResults"
      :has-searched="hasSearched"
      :inputs="inputs"
      :loading="loading"
      :links="links"
      :meta="meta"
      :page-ready="pageReady"
      :params="params"
      :reset="reset"
      :results="results"
      :search="search"
    />
  </div>
</template>

<script>
import { library } from '@fortawesome/fontawesome-svg-core';
import {
  faGhost,
  faSlidersH,
} from '@fortawesome/pro-light-svg-icons';
import assign from 'lodash/assign';
import clone from 'lodash/clone';
import get from 'lodash/get';
import padStart from 'lodash/padStart';
import pick from 'lodash/pick';
import size from 'lodash/size';
import { DateTime } from 'luxon';
import { mapActions, mapGetters, mapState } from 'vuex';

import useBrowserApiHelpers from '@/composables/use-browser-api-helpers';
import useErrorHandling from '@/composables/use-error-handling';
import useSearchData from '@/composables/use-search-data';
import useSmartTime from '@/composables/use-smart-time';
import SearchType from '@/enums/SearchType';
import { slugify } from '@/helpers/url';
import {
  getLocaleSlug,
  isMarketSiteCountry,
} from '@/i18n';
import LocalizesRoutes from '@/mixins/LocalizesRoutes';
import TrackSearches from '@/mixins/TrackSearches';

library.add({
  faGhost,
  faSlidersH,
});

export default {
  name: 'VetsterSearchBeta',

  mixins: [
    LocalizesRoutes,
    TrackSearches,
  ],

  props: {
    country: {
      type: String,
      required: true,
    },
    filters: {
      type: [Array, Object],
      required: true,
    },
    geocode: {
      type: Boolean,
      default: false,
    },
    locale: {
      type: String,
      required: true,
    },
    logoPath: {
      type: String,
      required: true,
    },
  },

  setup() {
    const { localStorageAvailable } = useBrowserApiHelpers();
    const { getError } = useErrorHandling();
    const { validSearchData } = useSearchData();
    const {
      searchTimeFrom,
      searchTimeTo,
      validDateFrom,
      validDateTimeSearchData,
    } = useSmartTime();

    return {
      getError,
      localStorageAvailable,
      searchTimeFrom,
      searchTimeTo,
      validDateFrom,
      validDateTimeSearchData,
      validSearchData,
    };
  },

  data() {
    return {
      errors: '',
      fallbackResults: [],
      hasChanged: false,
      hasSearched: false,
      loading: true,
      links: [],
      meta: [],
      pageReady: false,
      params: [],
      results: [],
      timezone: DateTime.local().zoneName,
    };
  },

  computed: {
    ...mapGetters(['getSiteCurrency']),

    ...mapGetters('geolocation', ['getGeolocation']),

    ...mapGetters('search', [
      'canSearch',
      'displayDate',
      'hasName',
    ]),

    ...mapState('search', [
      'inputs',
      'page',
    ]),

    hasResults() {
      return size(this.results) > 0;
    },

    hasFallbackResults() {
      return size(this.fallbackResults) > 0;
    },

    displayCity() {
      if (this.inputs.city && this.inputs.province) {
        return `${this.inputs.city}, ${this.inputs.province}`;
      }

      return this.$t('add-your-location');
    },
  },

  watch: {
    inputs: {
      handler() {
        this.hasChanged = true;

        this.$store.commit('search/setPage', 1);
        this.search();

        try {
          if (this.localStorageAvailable()) {
            localStorage.searchParams = JSON.stringify(this.inputs);
          }
        } catch {
          //
        }
      },
      deep: true,
    },
    page() {
      this.search();
    },
  },

  async created() {
    this.$store.commit('setLogoPath', this.logoPath);
    let searchData = {};
    if (size(this.filters)) {
      searchData = this.filters;

      if (!this.inputs.timezone) {
        searchData.timezone = this.timezone;
      }
    } else {
      searchData = this.paramsFromLocalStorage();
    }

    const validSearchData = this.validSearchData(searchData);
    const validDateTimeParams = this.validDateTimeSearchData(searchData);
    // This is until we support multiple currencies on the same market site
    const validCurrency = { currency: this.getSiteCurrency };
    searchData = Object.assign(searchData, validSearchData, validDateTimeParams, validCurrency);
    searchData = await this.resetLocationOnCountryMismatch(searchData);

    const geoCode = await this.fetchGeoWhenMissing(searchData);
    if (geoCode) {
      searchData = assign(searchData, geoCode);
      searchData = await this.updateLocationOnCountryMismatch(searchData);
    }

    await this.$store.commit('search/setInputs', searchData);

    this.setDatesFromInput();

    window.addEventListener('popstate', this.handlePopstate);

    this.pageReady = true;
    this.loading = false;
  },

  mounted() {
    this.trackSearchLoad(SearchType.AdvancedSearch);
  },

  destroyed() {
    window.removeEventListener('popstate', this.handlePopstate);
  },

  methods: {
    ...mapActions('geolocation', ['fetchGeolocation']),

    async resetLocationOnCountryMismatch(searchData) {
      if (!searchData.country || this.country === searchData.country) {
        return searchData;
      }

      return assign(searchData, {
        country: null,
        city: null,
        province: null,
      });
    },

    async updateLocationOnCountryMismatch(searchData) {
      if (!searchData.country || this.country === searchData.country) {
        return searchData;
      }

      const searchCountry = searchData.country.toString().toLowerCase();

      // TODO: Remove this line?
      sessionStorage.removeItem('blockLocaleRedirect');

      if (isMarketSiteCountry(searchCountry)
        && !sessionStorage.getItem('blockLocaleRedirect')
      ) {
        if (await this.$confirm(
          this.$t('search.it-looks-like-you-may-be-located-in', { location: this.$t(`country-code.${searchCountry}`) }),
          this.$t('app.search-by-location'),
          {
            confirmText: `Change back to ${this.$t(`country-code.${searchCountry}`)}`,
            cancelText: 'No, search here instead',
            icon: ['fal', 'map-marker'],
          },
        )) {
          this.redirectToMarketSite(searchCountry);
        } else {
          this.storeUserRedirectionKey();
        }
      }

      return assign(searchData, {
        country: null,
        city: null,
        province: null,
      });
    },

    storeUserRedirectionKey() {
      sessionStorage.setItem('blockLocaleRedirect', '1');
    },

    redirectToMarketSite(searchCountry) {
      window.location.href = `/${getLocaleSlug(searchCountry)}/s`;
    },

    async fetchGeoWhenMissing(searchData) {
      if (!this.geocode || (searchData.province && searchData.city)) {
        return null;
      }

      try {
        if (this.localStorageAvailable()) {
          if (localStorage.skipNextGeocode) {
            localStorage.removeItem('skipNextGeocode');

            return null;
          }
        }
      } catch {
        //
      }

      this.loading = true;

      await this.fetchGeolocation();

      try {
        if (this.getGeolocation.country) {
          return {
            city: this.getGeolocation.city,
            province: this.getGeolocation.province,
            country: this.getGeolocation.country.toLowerCase(),
          };
        }

        return null;
      } finally {
        this.loading = false;
      }
    },

    handleNext(inputs) {
      if (inputs.page <= 0) {
        inputs.page = 1;
      }
      this.$store.commit('search/setPage', inputs.page);
      this.search();
    },

    handlePrev(inputs) {
      if (inputs.page <= 0) {
        inputs.page = 1;
      }
      this.$store.commit('search/setPage', inputs.page);
      this.search();
    },

    async search(
      values = { skipHashUpdate: false, payload: this.getPayload() },
    ) {
      if (!this.canSearch) {
        return;
      }

      const { skipHashUpdate, payload } = values;
      const searchRoute = this.getSearchRoute();

      this.loading = true;

      await this.$api
        .post(searchRoute, payload)
        .then(
          (response) => {
            this.errors = '';
            this.results = response.data.data;
            this.meta = response.data.meta;
            this.links = response.data.links;
            this.params = response.data.params;

            if (!skipHashUpdate) {
              window.history.pushState(
                payload,
                this.$t('search.vetster-search-results'),
                this.generateUrl(response.data.params),
              );
            }
          },
          (error) => {
            this.errors = this.getError(error);
            this.loading = false;
          },
        )
        .finally(async () => {
          this.hasSearched = true;
          this.hasChanged = false;

          if (this.hasResults) {
            this.loading = false;
          } else {
            await this.fallbackSearch();
          }
        });
    },
    async fallbackSearch() {
      this.loading = true;

      await this.$api
        .post(this.getSearchRoute(), this.getFallbackPayload())
        .then(
          (response) => {
            this.errors = '';
            this.fallbackResults = response.data.data;
          },
          (error) => {
            this.errors = this.getError(error);
            this.loading = false;
          },
        )
        .finally(() => {
          this.loading = false;
        });
    },

    async handlePopstate(event) {
      if (!size(event.state)) {
        return;
      }

      this.hasChanged = true;

      let payload;

      if (size(event.state.full_name)) {
        payload = pick(event.state, [
          'full_name',
          'country',
          'province',
          'platform',
          'page',
        ]);
      } else {
        payload = event.state;
        delete payload.full_name;
        await this.$store.commit('search/setInputs', { full_name: '' });
      }

      await this.$store.commit('search/setInputs', payload);
      this.setDatesFromInput();
      await this.search({ skipHashUpdate: true, payload: this.getPayload() });
    },

    setDatesFromInput() {
      this.$store.commit('search/setDates', {
        date_from: this.inputs.date_from,
        date_to: this.inputs.date_to,
        timezone: this.inputs.timezone,
      });
    },

    getPayload() {
      let payload;

      if (this.hasName) {
        payload = pick(this.inputs, [
          'full_name',
          'country',
          'province',
          'platform',
        ]);
      } else {
        payload = clone(this.inputs); // Prevent updating original data
        payload.time_to = this.timeAsString(this.inputs.time_to);
        payload.time_from = this.timeAsString(this.inputs.time_from);
        delete payload.full_name;
      }

      payload.page = this.page;

      return payload;
    },
    getFallbackPayload() {
      const payload = this.getPayload();
      payload.page = 1;
      payload.language = 'en';
      payload.time_from = this.timeAsString(this.searchTimeFrom());
      payload.time_to = this.timeAsString(this.searchTimeTo());
      payload.date_from = this.validDateFrom();
      delete payload.full_name;
      delete payload.areas_of_interest;
      delete payload.species_treated;

      return payload;
    },

    getSearchRoute() {
      if (this.hasName) {
        return 'p/search/name';
      }

      return 'p/search';
    },

    reset() {
      try {
        if (this.localStorageAvailable()) {
          localStorage.removeItem('searchParams');
          localStorage.skipNextGeocode = 1;
        }
      } catch {
        //
      }

      window.location.href = this.localizeUrl('s');
    },

    timeAsString(time) {
      const timeString = padStart(time || 0, 4, '0');

      return `${timeString.substring(0, 2)}:${timeString.substring(2, 4)}`;
    },

    paramsFromLocalStorage() {
      let localParams = {};

      try {
        if (this.localStorageAvailable()) {
          if (typeof localStorage.searchParams !== 'undefined') {
            localParams = JSON.parse(localStorage.searchParams);
          }
        }
      } catch {
        //
      }

      const validDateTimeParams = this.validDateTimeSearchData(localParams);

      return {
        date_from: get(validDateTimeParams, 'date_from', ''),
        date_to: get(localParams, 'date_to', ''),
        time_from: get(localParams, 'time_from', 800),
        time_to: get(localParams, 'time_to', 2000),
        timezone: get(localParams, 'timezone', this.timezone),
        city: get(localParams, 'city', ''),
        province: get(localParams, 'province', ''),
        currency: get(localParams, 'currency', this.getSiteCurrency),
        country: get(localParams, 'country', this.country),
        language: get(localParams, 'language', this.language) || 'en',
        full_name: get(localParams, 'full_name', ''),
        areas_of_interest: get(localParams, 'areas_of_interest', []),
        species_treated: get(localParams, 'species_treated', []),
      };
    },

    generateUrl(filters) {
      const url = `/s/${slugify(
        `Vets in ${[filters.city, filters.province, filters.country]
          .filter(Boolean)
          .join(', ')}`,
      )}`;
      delete filters.platform;
      delete filters.currency;
      const queryString = Object.keys(filters)
        .map((key) => {
          if (key === 'date_from' || key === 'date_to') {
            filters[key] = DateTime.fromISO(filters[key], {
              zone: filters.timezone,
            }).toISODate();
          }
          if (key === 'time_from' || key === 'time_to') {
            filters[key] = filters[key].replace(':', '');
          }

          return `${encodeURIComponent(key)}=${encodeURIComponent(
            filters[key],
          )}`;
        })
        .join('&');

      return `${this.localizeUrl(url)}?${queryString}`;
    },
  },
};
</script>
